import { FunctionComponent, ReactElement, useState } from "react";

import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";

import { LoadingButton } from "@mui/lab";

import { useBulkImportIngredients } from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import { IngredientBaseUnit } from "../../sdk/types/api";
import { TBulkIngredientPayload } from "../../sdk/utils/api/ingredients";

import { ingredientQueryKeys } from "./constants";

export interface IImportIngredientsDialogProps {
    open: boolean;
    onClose: () => void;
}

const BASE_UNITS: Record<string, IngredientBaseUnit> = {
    g: IngredientBaseUnit.GRAM,
    ml: IngredientBaseUnit.MILLILITRE,
    pcs: IngredientBaseUnit.PIECE,
};

const TEMPLATE = "name,category,baseUnit,gramsPerPiece,isStaple,aliases\nPaneer,dairy,g,,no,cottage cheese\nOnion,vegetables,g,150,no,pyaz|red onion";

/**
 * One ingredient per line: name, category slug, base unit (g/ml/pcs),
 * grams per piece, staple (yes/no), aliases separated by "|".
 */
const parseCsv = (text: string): { items: TBulkIngredientPayload[]; errors: string[] } => {
    const errors: string[] = [];
    const items: TBulkIngredientPayload[] = [];
    text
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line, index) => {
            if (index === 0 && line.toLowerCase().startsWith("name,")) {
                return;
            }
            const [name, category, baseUnit, gramsPerPiece, isStaple, aliases] = line
                .split(",")
                .map((cell) => cell.trim());
            if (!name) {
                errors.push(`Line ${index + 1}: name is missing`);
                return;
            }
            const unit = BASE_UNITS[(baseUnit || "g").toLowerCase()];
            if (!unit) {
                errors.push(`Line ${index + 1}: base unit must be g, ml or pcs`);
                return;
            }
            items.push({
                name,
                categorySlug: category || null,
                baseUnit: unit,
                gramsPerPiece: gramsPerPiece ? Number(gramsPerPiece) : null,
                isStaple: ["yes", "true", "1"].includes((isStaple ?? "").toLowerCase()),
                aliases: (aliases ?? "")
                    .split("|")
                    .map((alias) => alias.trim())
                    .filter(Boolean),
            });
        });
    return { items, errors };
};

export const ImportIngredientsDialog: FunctionComponent<IImportIngredientsDialogProps> = (
    props: IImportIngredientsDialogProps,
): ReactElement => {
    const { open, onClose } = props;
    const [text, setText] = useState("");
    const { showError, showSuccess } = useMessage();
    const importMutation = useBulkImportIngredients(ingredientQueryKeys.list);

    const { items, errors } = parseCsv(text);

    const handleImport = async () => {
        try {
            const result = await importMutation.mutateAsync({ items });
            showSuccess(
                `Imported: ${result.created} created, ${result.updated} updated` +
                    (result.skipped.length ? `, ${result.skipped.length} skipped` : ""),
            );
            if (result.skipped.length === 0) {
                setText("");
                onClose();
            } else {
                setText(result.skipped.join("\n"));
            }
        } catch (error) {
            showError(error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Import ingredients</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Paste CSV rows. Existing ingredients (matched by name) are updated.
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    minRows={10}
                    placeholder={TEMPLATE}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    sx={{ fontFamily: "monospace", mt: 1 }}
                />
                {errors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {errors.slice(0, 5).join(" · ")}
                        {errors.length > 5 ? ` · and ${errors.length - 5} more` : ""}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setText(TEMPLATE)}>Use template</Button>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton
                    variant="contained"
                    disabled={items.length === 0 || errors.length > 0}
                    loading={importMutation.isLoading}
                    onClick={handleImport}
                >
                    {`Import ${items.length || ""}`.trim()}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
